/** Pure helpers for the AI models list: status, filters, sorting and the test tooltip. */

import type { AiModel, ModelTestResult } from '@/api/types';

export type Status = 'ready' | 'attention' | 'disabled';
export const STATUSES: { value: Status; label: string }[] = [
  { value: 'ready', label: 'Ready' },
  { value: 'attention', label: 'Needs attention' },
  { value: 'disabled', label: 'Disabled' },
];

export function statusOf(m: AiModel): Status {
  if (!m.enabled) return 'disabled';
  return m.unusable_reason ? 'attention' : 'ready';
}

export type SortKey = 'display_name' | 'provider' | 'model_id' | 'price_input' | 'price_output' | 'enabled';
export interface ModelSort {
  key: SortKey;
  order: 'asc' | 'desc';
}

/** What the caller may do on a model row. */
export interface RowAccess {
  manage: boolean;
  test: boolean;
}

/** The actions column shows only when the caller can do something on a row. */
export function columnCount(access: RowAccess): number {
  return access.manage || access.test ? 7 : 6;
}

export interface ModelFilters {
  search: string;
  providers: string[];
  statuses: string[];
}

function matchesSearch(m: AiModel, needle: string): boolean {
  return !needle || m.display_name.toLowerCase().includes(needle) || m.model_id.toLowerCase().includes(needle);
}

function matchesAny(selected: string[], value: string): boolean {
  return selected.length === 0 || selected.includes(value);
}

export function filterModels(rows: AiModel[], filters: ModelFilters): AiModel[] {
  const needle = filters.search.trim().toLowerCase();
  return rows.filter(
    (m) => matchesSearch(m, needle) && matchesAny(filters.providers, m.hosting) && matchesAny(filters.statuses, statusOf(m)),
  );
}

function sortValues(hostingLabel: Record<string, string>): Record<SortKey, (m: AiModel) => string | number> {
  return {
    display_name: (m) => m.display_name.toLowerCase(),
    provider: (m) => (hostingLabel[m.hosting] ?? m.hosting).toLowerCase(),
    model_id: (m) => m.model_id.toLowerCase(),
    price_input: (m) => Number(m.price_input ?? -1),
    price_output: (m) => Number(m.price_output ?? -1),
    enabled: (m) => (m.enabled ? 0 : 1),
  };
}

function compareValues(x: string | number, y: string | number): number {
  return typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
}

/** Sorts in place, like Array.sort. */
export function sortModels(rows: AiModel[], sort: ModelSort, hostingLabel: Record<string, string>): AiModel[] {
  const value = sortValues(hostingLabel)[sort.key];
  const direction = sort.order === 'asc' ? 1 : -1;
  return rows.sort((a, b) => direction * compareValues(value(a), value(b)));
}

/** Clicking the sorted column flips its order; another column starts ascending. */
export function nextSort(s: ModelSort, key: SortKey): ModelSort {
  if (s.key !== key) return { key, order: 'asc' };
  return { key, order: s.order === 'asc' ? 'desc' : 'asc' };
}

export function testTooltip(model: AiModel, pending: boolean): string {
  if (model.unusable_reason) return 'Cannot test: see the note below';
  return pending ? 'Testing…' : 'Test (billed and logged)';
}

/** The request error, or the provider's error from a failed test. */
export function testFailure(error: Error | null, result?: ModelTestResult): string | null {
  if (error) return error.message;
  return result && !result.ok ? (result.error ?? null) : null;
}
